/**
 * @fileoverview AR-670 — static fixtures of the mock Classroom adapter,
 * following the READ-ONLY backend contract (branch
 * feat-classroom-link-only-courses; see ../types.ts). The mock is kept for
 * Jest and for developing screens without the backend; runtime uses the HTTP
 * adapter.
 */

import { ClassroomCourseOption, RosterStudent, RosterSummary, RosterUnmatchedStudent } from '../types'
import { LinkedCourseRow, StudentClassroomFile } from '../phase3Types'

export const MOCK_COURSES: ClassroomCourseOption[] = [
  {
    id: '101',
    name: 'Turma A - Water in Our World - 26/27',
    section: 'Period 2',
    courseState: 'ACTIVE',
    alternateLink: 'https://classroom.google.com/c/mock-101',
    linkedProject: null,
  },
  {
    id: '102',
    name: 'Turma A - Global Health Forum - 26/27',
    section: null,
    courseState: 'ACTIVE',
    alternateLink: 'https://classroom.google.com/c/mock-102',
    // Already taken — the picker must offer it disabled/filtered (1:1 rule).
    linkedProject: { id: 'project-102', name: 'Global Health Forum' },
  },
  {
    id: '103',
    name: 'Turma B - Designing a Social Enterprise - 25/26',
    section: 'Morning',
    courseState: 'ARCHIVED',
    alternateLink: 'https://classroom.google.com/c/mock-103',
    linkedProject: null,
  },
]

/** Matched students of the persisted roster (GET) — always "enrolled" now. */
const enrolled = (studentId: string, name: string, email: string): RosterStudent => ({
  studentId,
  name,
  email,
  status: 'enrolled',
  failureReason: null,
})

export const MOCK_ROSTER_STUDENTS: RosterStudent[] = [
  enrolled('p1', 'Ronny Weasley', 'ronny.weasley@arcmosaico.ai'),
  enrolled('p2', 'Hermione Granger', 'hermione.granger@arcmosaico.ai'),
  enrolled('p3', 'Luna Lovegood', 'luna.lovegood@arcmosaico.ai'),
]

/** Project students the matching could not map. */
export const MOCK_UNMATCHED_STUDENTS: RosterUnmatchedStudent[] = [
  { studentId: 'p4', name: 'Neville Longbottom', email: null, reason: 'no_email' },
  { studentId: 'p5', name: 'Cho Chang', email: 'cho.chang@gmail.com', reason: 'not_found_in_course' },
]

/** Summary of a matching run (link / sync_roster) — drives the toaster. */
export const MOCK_ROSTER_SUMMARY: RosterSummary = {
  mappedStudentsCount: 3,
  matchedCount: 3,
  unmatchedCount: 2,
  unmatched: MOCK_UNMATCHED_STUDENTS,
}

export const MOCK_ROSTER_SUMMARY_ALL_UNMATCHED: RosterSummary = {
  mappedStudentsCount: 0,
  matchedCount: 0,
  unmatchedCount: 5,
  unmatched: [
    ...MOCK_UNMATCHED_STUDENTS,
    ...MOCK_ROSTER_STUDENTS.map(student => ({
      studentId: student.studentId,
      name: student.name,
      email: student.email,
      reason: 'not_found_in_course' as const,
    })),
  ],
}

/** Rows of the "Linked courses" table (project↔course links). The second row
 *  exercises the orphan-link state (its tutor disconnected). */
export const MOCK_LINKED_COURSES: LinkedCourseRow[] = MOCK_COURSES.slice(0, 2).map((course, index) => ({
  id: `link-${course.id}`,
  googleCourseId: course.id,
  name: course.name,
  alternateLink: course.alternateLink ?? null,
  origin: index === 0 ? 'created' : 'linked',
  syncEnabled: index === 0,
  mappedStudentsCount: index === 0 ? 12 : 0,
  lastRosterSyncAt: index === 0 ? '2026-07-30T12:00:00.000Z' : null,
  connectionStatus: index === 0 ? ('connected' as const) : ('disconnected' as const),
  connectionGoogleEmail: index === 0 ? 'teacher@arcmosaico.ai' : null,
  groupName: index === 0 ? 'Turma A' : null,
  project: {
    id: `project-${course.id}`,
    name: course.name.split(' - ')[1] || course.name,
    status: 'approved',
  },
}))

/** Files the student posted on the course stream (announcements). */
export const MOCK_STUDENT_FILES: StudentClassroomFile[] = [
  {
    kind: 'drive_file',
    name: 'water-cycle-poster.pdf',
    url: 'https://drive.google.com/file/d/mock-drive-1',
    iconUrl: null,
    postedAt: '2026-08-20T10:00:00Z',
  },
  {
    kind: 'youtube',
    name: 'Interview recording',
    url: 'https://youtu.be/mock-interview',
    iconUrl: null,
    postedAt: '2026-08-21T15:30:00Z',
  },
]
